import { Injectable } from '@angular/core';

import { DprForecastActivityGridData } from '@dpr-details/models/grid/dpr-forecast-activity-grid-data.model';
import { Location } from '@shared/models/project-setup';
import { ProjectSetup } from '@shared/models';
import {
  Activity,
  ActivityGroup,
  ForecastCategory,
} from '@shared/models/project-setup';
import { convertDateToTimeString, defaultStartTime } from '@shared/utils';
import { DprActivitiesGridService } from './grid';

@Injectable()
export class DprForecastActivityCreateService {
  constructor(private dprActivitiesGridService: DprActivitiesGridService) {}

  createForecastActivity(
    dprDetailId: number | string,
    lastRow: DprForecastActivityGridData,
    projectSetup: ProjectSetup
  ): Partial<DprForecastActivityGridData> {
    const forecastCategory = this.computeForecastCategory(
      lastRow?.forecastCategoryId,
      projectSetup.forecastCategories
    );

    let activityGroup = this.computeActivityGroup(
      lastRow?.activityGroupId,
      projectSetup.activityGroupList
    );

    const activity = this.computeActivity(
      activityGroup?.id,
      lastRow?.activityId,
      projectSetup.activityList
    );

    const location = this.computeLocation(
      activity?.id,
      lastRow?.locationId,
      projectSetup.locationList,
      projectSetup.activityList
    );

    const activityGroupOptions = this.getActivityGroupOptions(
      projectSetup.activityGroupList
    );

    if (!activity && activityGroupOptions.length > 1) {
      activityGroup = null;
    }

    const start = this.computeStart(lastRow);
    const finish = activity?.isMilestone ? start : null;

    const result: Partial<DprForecastActivityGridData> = {
      dprDetailId,
      forecastCategoryId: forecastCategory?.id,
      activityGroupId: activityGroup?.id,
      activityId: activity?.id,
      locationId: location?.id,
      date: lastRow?.date,
      start,
      finish,
      comment: '',
      dprForecastSubActivities: [],
      dprForecastActivityCustomDataList: [],
      canBeDeleted: true,
    };

    return result;
  }

  private computeForecastCategory(
    forecastCategoryId: number,
    forecastCategories: ForecastCategory[]
  ): ForecastCategory {
    const forecastCategory = forecastCategories.find(
      (x) => x.id === forecastCategoryId
    );

    // return last selected forecast category
    if (forecastCategory?.isActive) {
      return forecastCategory;
    }

    const options = forecastCategories.filter((x) => x.isActive);

    return options.length === 1 ? options[0] : null;
  }

  private computeActivityGroup(
    activityGroupId: number,
    activityGroups: ActivityGroup[]
  ): ActivityGroup {
    const activityGroup = activityGroups.find((x) => x.id === activityGroupId);

    // return last selected activity
    if (activityGroup?.isActive) {
      return activityGroup;
    }

    const options = this.getActivityGroupOptions(activityGroups);

    return options.length === 1 ? options[0] : null;
  }

  /**
   * @description Try to preselect an active activity in the following order:
   * 1. If the last selected activity is still an option and it is active, select it
   * 2. If there is just one option available, select it
   * 3. Try to find the next active option based on priority
   */
  private computeActivity(
    activityGroupId: number,
    activityId: number,
    activities: Activity[]
  ): Activity {
    const options = this.dprActivitiesGridService.getActivityOptions(
      activityGroupId,
      activities
    );

    const activityIndex = options.findIndex((x) => x.id === activityId);

    if (activityIndex !== -1 && activityIndex + 1 < options.length) {
      return options[activityIndex + 1];
    }

    const activity = options.find((x) => x.id === activityId);

    if (activity) {
      return activity;
    }

    if (options.length === 1) {
      return options[0];
    }

    const priority = activities.find((x) => x.id === activityId)?.priority;

    return options.find((x) => x.priority > priority);
  }

  /**
   * @description Try to preselect an active location in the following order:
   * 1. If the last selected location is still an option, select it
   * 2. If there is just one option available, select it
   */
  private computeLocation(
    activityId: number,
    locationId: number,
    locations: Location[],
    activities: Activity[]
  ): Location {
    const options = this.dprActivitiesGridService.getLocationOptions(
      activityId,
      locations,
      activities
    );

    const location = options.find((x) => x.id === locationId);

    if (location) {
      return location;
    }

    return options.length === 1 ? options[0] : null;
  }

  /**
   * @description Computes the start time based on the previous row finish time
   */
  private computeStart(previousRow: DprForecastActivityGridData): string {
    if (previousRow?.finish) {
      return previousRow?.finish;
    }

    if (!previousRow) {
      return convertDateToTimeString(defaultStartTime());
    }

    return null;
  }

  private getActivityGroupOptions(
    activityGroups: ActivityGroup[]
  ): ActivityGroup[] {
    return activityGroups.filter((x) => x.isActive).sort();
  }
}
